import { api } from "./api";
import type { RequestBody, Result } from "./spec";
import type {
  AccountType,
  Room,
  RoomWithInfo,
  RoomMember,
  RoomMemberFilter,
  RoomFilter,
  RoomScope,
  MatchQueue,
  MatchQueueStatus,
  MatchQueuePhase,
  MatchType,
} from "./types";

export type { MatchQueue, MatchQueuePhase, MatchQueueStatus, MatchType, RoomFilter, RoomScope };

export enum RoomType {
  STRING = 1,
  KNOT   = 2,
  LOOP   = 3,
}

/**
 * True for a Loop — a public, topic-based room with no join step and no members.
 * See CONTEXT.md: a Loop is not a kind of Knot, and the two differ in exactly
 * the fields the Knot projection derives.
 */
export function isLoop(room: Pick<Room, 'type'>): boolean {
  return room.type === RoomType.LOOP;
}

/**
 * `domain.RoomScope` is a string union in the spec, so it has no generated runtime
 * value. These constants give callers something to import instead of retyping the
 * literals, and keep the set discoverable next to RoomType.
 */
export const RoomScopes = {
  UNIVERSE:       "UNIVERSE",
  UNIVERSITY:     "UNIVERSITY",
  COMPANY:        "COMPANY",
  OWN_UNIVERSITY: "OWN_UNIVERSITY",
  OWN_COMPANY:    "OWN_COMPANY",
} as const satisfies Record<string, RoomScope>;

/** Scopes that mean "restricted to the viewer's own organisation". */
const OWN_SCOPES: readonly RoomScope[] = [RoomScopes.OWN_UNIVERSITY, RoomScopes.OWN_COMPANY];

/**
 * The "my organisation" scope for an account: students belong to a university,
 * workers to a company. Used to resolve the org filter pill to a server-side
 * `scope` value — the same pill means different things for the two account types.
 */
export function ownScopeFor(accountType: AccountType | undefined): RoomScope {
  return accountType === "WORKER" ? RoomScopes.OWN_COMPANY : RoomScopes.OWN_UNIVERSITY;
}

/** True when `scope` restricts a room to a single organisation (uni or company). */
export function isOrgScoped(scope: RoomScope | undefined): boolean {
  return !!scope && OWN_SCOPES.includes(scope);
}

export enum RoomRequestStatus {
  PENDING_USER       = 0,
  PENDING_ROOM       = 1,
  APPROVED           = 2,
  DENIED             = 3,
  // Legacy aliases — still referenced throughout the web UI; remove in a follow-up cleanup
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  REQEUESTED_TO_USER = 0,
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  REQUQESTED_TO_ROOM = 1,
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  REQUEST_APPROVED   = 2,
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  REQUEST_DENIED     = 3,
}
/** @deprecated use RoomRequestStatus */
export { RoomRequestStatus as RoomRequestStatusEnum }

// ─── Inline request/response types ────────────────────────────────────────────




/** Response of `room/invite` — `maxUses` is a *request* field, not a response one. */
export type GenerateInviteResult = Result<"room/invite", "post">;


/** What an invite link shows, expired or not: `room/invite/{token}`. */
export type InvitePreview = Result<"room/invite/{token}", "get">;

/** What a Knot link shows anyone: `room/k/{code}`. `viewer` is absent signed out; `members` absent for an anonymous Knot under ten. */
export type KnotCard = Result<"room/k/{code}", "get">;

/** What came of a join: in (`joined`), asked (`requested`), or in the next batch (`waiting`). */
export type KnotJoinResult = Result<"room/k/{code}/join", "post">;

/** The Knots the caller waits for (`room/waiting`): each a card with `viewer: "waiting"`, `closesAt` while a window is open, `code` while a link is live. */
export type WaitingKnot = Result<"room/waiting", "get">[number];

/** A public Knot's shareable link: `room/link`. */
export type KnotLink = Result<"room/link", "post">;

export type RoomCreateResult = RoomWithInfo

/** @deprecated use CreateRoomReq */
export type CreateRoom = CreateRoomReq

export interface Knot {  // exported for use in types.ts
  id: string;
  emoji: string;
  name: string;
  desc: string;
  lastMessage?: string;
  /** With a blank `lastMessage`, says the newest message was unsent. */
  lastMessageId?: number;
  updatedAt?: string;
  /** Identity mode of the room — not `domain.RoomType`, see `roomType` for that. */
  type: 'anon' | 'real';
  members: number;
  /** @deprecated collapses COMPANY scopes into "uni"; read `scope` instead */
  uniOnly: boolean;
  /** Raw server scope, so the UI can tell OWN_UNIVERSITY from OWN_COMPANY. */
  scope?: RoomScope;
  /** The institution an `OWN_*` scope admits, for "{institution} only". */
  scopeLabel?: string | null;
  /** Raw `domain.RoomType` (STRING | KNOT), preserved for list-level filtering. */
  roomType?: Room['type'];
  isPublic?: boolean;
  featured: boolean;
  createdAt: string;
  roomUserId?: string;
  active?: number;
  /** Ask to join is on: joining a public real-name Knot sends a request. */
  askToJoin?: boolean;
  /** Where a non-member stands to joining (`room/discover`). */
  viewer?: Room['viewer'];
  /** An anonymous Knot of fewer than ten sends no count (`members: null`): say "Fewer than 10". */
  membersHidden?: boolean;
}

export function roomToKnot(room: Room, info?: RoomMember): Knot {
  // A Loop has no members and no join step, so `members`, `roomUserId` and
  // `uniOnly` would all be fabrications. Failing loudly here keeps a Loop from
  // reaching a Knot list, where it would render a Join action that cannot work.
  if (isLoop(room)) {
    throw new Error(`roomToKnot: room ${room.id ?? '(no id)'} is a Loop, not a Knot`);
  }
  return {
    id: room.id ?? '',
    emoji: room.meta?.emoji ?? '🔘',
    name: room.name ?? '',
    desc: room.description ?? '',
    lastMessage: room.lastMessage,
    lastMessageId: room.lastMessageId,
    updatedAt: room.updatedAt,
    type: room.isAnonymous ? 'anon' : 'real',
    members: room.members ?? 0,
    uniOnly: room.scope === RoomScopes.UNIVERSITY || room.scope === RoomScopes.OWN_UNIVERSITY,
    scope: room.scope,
    scopeLabel: room.scopeLabel,
    roomType: room.type,
    isPublic: room.isPublic,
    featured: false,
    createdAt: room.createdAt ?? '',
    roomUserId: info?.userId ?? (room.isMember ? 'member' : undefined),
    askToJoin: room.askToJoin,
    viewer: room.viewer,
    membersHidden: room.members == null && !!room.isAnonymous,
  };
}


// ─── Legacy local interfaces (kept for backwards compat) ──────────────────────







// ─── Request shapes (spec-derived) ────────────────────────────────────────────

export type CreateRoomReq = RequestBody<"room", "post">;
export type UpdateRoomInfoReq = RequestBody<"room/info/update", "post">;
export type GenerateInviteReq = RequestBody<"room/invite", "post">;
export type RoomRequestFilter = RequestBody<"room/members/request", "post">;
export type GetAnonymousRoom = RequestBody<"room/anonymous", "post">;
export type GetRooms = RequestBody<"room/list", "post">;
export type GetRoom = RequestBody<"room/info", "post">;
export type AddRoomMember = RequestBody<"room/members/add", "post">;
export type KickKnotMemberReq = RequestBody<"room/members/knot/kick", "post">;
export type LeaveRoomReq = RequestBody<"room/members/leave", "post">;
export type AskToJoinReq = RequestBody<"room/ask-to-join", "post">;
export type JoinRoomReq = RequestBody<"room/join", "post">;
export type DeleteRoomRequestReq = RequestBody<"room/members/request/delete", "post">;
export type UpdateRoomRequest = RequestBody<"room/members/request/update", "post">;

/** @deprecated use RoomMemberFilter */
export type GetRoomMembers = RoomMemberFilter;
/** @deprecated use RoomRequestFilter */
export type GetRoomRequests = RoomRequestFilter;

// ─── Service ──────────────────────────────────────────────────────────────────

const roomService = {
  // Single-object responses are annotated with the app-level types from
  // types.ts (spec type + optional UI-only extras). The spec type is assignable
  // to those intersections, so the augmentation survives without a cast.
  /*
    CreateRoom answers with a wrapper, not a bare room: `{ room, roomUserId }`.
    The swagger annotation used to claim a bare `domain.Room`, so this was typed
    as one and both call sites read `.id` straight off the response — which was
    always undefined at runtime. The backend now documents the real shape
    (`chat.swaggerCreateRoomRes`), so the wrapper is named here too.
  */
  createRoom: (body: CreateRoomReq) =>
    api.post("room", { body, hasAuth: true }),

  updateRoom: (body: UpdateRoomInfoReq) =>
    api.post("room/info/update", { body, hasAuth: true }),

  getRooms: (body: GetRooms) =>
    api.post("room/list", { body, hasAuth: true }),

  getRoom: (body: GetRoom): Promise<RoomWithInfo> =>
    api.post("room/info", { body, hasAuth: true }),

  /** @deprecated Use listMembers instead */
  getRoomMembers: (body: RoomMemberFilter) =>
    api.post("room/members", { body, hasAuth: true }),

  listMembers: (body: RoomMemberFilter) =>
    api.post("room/members", { body, hasAuth: true }),

  addRoomMember: (body: AddRoomMember) =>
    api.post("room/members/add", { body, hasAuth: true }),

  addKnotMember: (body: AddRoomMember) =>
    api.post("room/members/add", { body, hasAuth: true }),

  kickKnotMember: (body: KickKnotMemberReq) =>
    api.post("room/members/knot/kick", { body, hasAuth: true }),

  leaveRoom: (body: LeaveRoomReq) =>
    api.post("room/members/leave", { body, hasAuth: true }),

  /** @deprecated Use listRequests instead */
  getRoomRequests: (body: RoomRequestFilter) =>
    api.post("room/members/request", { body, hasAuth: true }),

  listRequests: (body: RoomRequestFilter) =>
    api.post("room/members/request", { body, hasAuth: true }),

  /** @deprecated Use updateRequest instead (typo in original name) */
  udpateRequest: (body: UpdateRoomRequest) =>
    api.post("room/members/request/update", { body, hasAuth: true }),

  updateRequest: (body: UpdateRoomRequest) =>
    api.post("room/members/request/update", { body, hasAuth: true }),

  /** Joins a public Knot from Explore or its info: `joined`, `requested` (it asks) or `waiting` (anonymous window). */
  joinRoom: (body: JoinRoomReq): Promise<Result<"room/join", "post">> =>
    api.post("room/join", { body, hasAuth: true }),

  /** The caller's own waiting Knots. */
  listWaiting: (): Promise<WaitingKnot[]> => api.get("room/waiting", { hasAuth: true }),

  /** Stop waiting for a Batch. 404 when not waiting there (a Batch revealed them first). */
  stopWaiting: (roomId: string) => api.post("room/waiting/leave", { body: { roomId }, hasAuth: true }),

  /** Withdraws the caller's own pending join request to a Knot (`room/join` does not return its id). */
  cancelRequest: (roomId: string) => api.post("room/members/request/delete", { body: { roomId }, hasAuth: true }),

  deleteRoomRequest: (body: DeleteRoomRequestReq) =>
    api.post("room/members/request/delete", { body, hasAuth: true }),

  deleteRequest: (body: DeleteRoomRequestReq) =>
    api.post("room/members/request/delete", { body, hasAuth: true }),

  getAnonymousRoom: (body: GetAnonymousRoom) =>
    api.post("room/anonymous", { body, hasAuth: true }),

  getAnonymousMatchStatus: (): Promise<MatchQueue> =>
    api.get("room/anonymous/status", { hasAuth: true }),

  /** Answers `{}` when it cancelled, otherwise the entry it found, so every field is optional. */
  cancelAnonymousMatch: () =>
    api.del("room/anonymous", { hasAuth: true }),

  discoverKnots: (filter: RoomFilter) =>
    api.post("room/discover", { body: filter, hasAuth: true }),

  createInviteLink: (body: GenerateInviteReq): Promise<GenerateInviteResult> =>
    api.post("room/invite", { body, hasAuth: true }),

  getInvitePreview: (token: string) =>
    api.get("room/invite/{token}", { path: { token } }),

  joinByInvite: (token: string) =>
    api.post("room/invite/{token}/join", { path: { token }, hasAuth: true }),

  /**
   * The Knot a link opens. Public; signed in, the bearer is sent too, so the card
   * says where the caller stands (`viewer`).
   */
  getKnotCard: (code: string, signedIn: boolean): Promise<KnotCard> =>
    api.get("room/k/{code}", { path: { code }, hasAuth: signedIn }),

  /** Joins by a link's code; `note` goes to the admin when joining asks. Business codes 3009, 3040-3042, 3044 reject; see `knot-page.ts`. */
  joinKnotByCode: (code: string, note?: string): Promise<KnotJoinResult> =>
    api.post("room/k/{code}/join", { path: { code }, body: note ? { note } : undefined, hasAuth: true }),

  /** The Knot admin's, on a public real-name Knot: while on, joining sends a join request. 3045 elsewhere. */
  setAskToJoin: (body: AskToJoinReq) =>
    api.post("room/ask-to-join", { body, hasAuth: true }),

  /** The Knot admin's: closes an anonymous Knot's Admission window now. `batch` is how many were revealed, `null` for none (never whether 0, 1 or 2 waited). */
  closeInvite: (roomId: string) => api.post("room/invite/close", { body: { roomId }, hasAuth: true }),

  /** A member of an anonymous private Knot: ask for a new Pseudonym at the next Batch (`pending: true`) or withdraw the ask. 3050 elsewhere. */
  requestRename: (roomId: string, pending: boolean) => api.post("room/rename", { body: { roomId, pending }, hasAuth: true }),

  /** The Knot admin's private-Knot link, still live (survives a restart). 404 when there is none. */
  getLiveInvite: (roomId: string): Promise<GenerateInviteResult> =>
    api.post("room/invite/live", { body: { roomId }, hasAuth: true }),

  /** Stops the live link and issues a new one with the same expiry and uses; `asksFirst` omitted keeps the setting. */
  resetInvite: (roomId: string, asksFirst?: boolean): Promise<GenerateInviteResult> =>
    api.post("room/invite/reset", { body: { roomId, asksFirst }, hasAuth: true }),

  /** Everyone under a Removal block, newest first (Pseudonyms in an anonymous Knot). */
  listRemoved: (roomId: string) =>
    api.post("room/members/removed", { body: { roomId }, hasAuth: true }),

  /** Lifts a Removal block; `userId` is the one `listRemoved` gave. */
  liftRemoval: (roomId: string, userId: string) =>
    api.post("room/members/removed/lift", { body: { roomId, userId }, hasAuth: true }),

  /** Any member's to read: the same link every time. 3043 when the Knot is not public. */
  getKnotLink: (roomId: string): Promise<KnotLink> =>
    api.post("room/link", { body: { roomId }, hasAuth: true }),

  /** Admin only: the old Knot link stops resolving and the new one is returned. 3043 when the Knot is not public. */
  rotateKnotLink: (roomId: string): Promise<KnotLink> =>
    api.post("room/link/rotate", { body: { roomId }, hasAuth: true }),

  // ─── Backwards-compat stubs ───────────────────────────────────────────────────

  /** @deprecated room/queue endpoints removed from spec; use cancelAnonymousMatch */
  cancelMatchQueue: (_id?: string) =>
    api.del("room/anonymous", { hasAuth: true }),

  /** @deprecated use leaveRoom */
  removeRoomMember: (body: { roomId: string; isLeaving?: boolean }) =>
    api.post("room/members/leave", { body: { roomId: body.roomId }, hasAuth: true }),

  /** @deprecated use getAnonymousRoom instead */
  enterMatchQueue: (body: GetAnonymousRoom) =>
    api.post("room/anonymous", { body, hasAuth: true }),

  /** @deprecated polling removed from spec */
  getMatchQueueStatus: (_id?: string | number): Promise<MatchQueue> =>
    api.get("room/anonymous/status", { hasAuth: true }),
};

export default roomService;
